import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { SnapshotTile } from '../../../common/model/type/clinical-snapshot.type';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvClinicalSnapshotTile from '../molecules/OvClinicalSnapshotTile';

interface OvClinicalSnapshotProps {
  heading: string;
  tiles: SnapshotTile[];
}

const OvClinicalSnapshot: FC<OvClinicalSnapshotProps> = ({
  heading,
  tiles,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <IdentityRow>
        <Eyebrow>
          <PersonOutlineIcon fontSize="small" />
          {t('patientDetails.snapshot.eyebrow')}
        </Eyebrow>
        <EyebrowDivider />
        <Heading>{heading}</Heading>
      </IdentityRow>

      <Tiles>
        {tiles.map(({ key, state }) => (
          <OvClinicalSnapshotTile key={key} tileKey={key} state={state} />
        ))}
      </Tiles>
    </Card>
  );
};

export default OvClinicalSnapshot;

const CARD_CONTAINER_NAME = 'snapshot';

const SIX_COLUMN_MIN_CARD_WIDTH_PX = 780;
const THREE_COLUMN_MAX_CARD_WIDTH_PX = SIX_COLUMN_MIN_CARD_WIDTH_PX - 1;
const TWO_COLUMN_MAX_CARD_WIDTH_PX = 639;

const SIX_COLUMNS_ORDER_COUPLED_TO_SNAPSHOT_TILE_ORDER = `
  minmax(3.75rem, 0.7fr)
  minmax(0, 1.3fr)
  minmax(0, 1.3fr)
  minmax(0, 1.05fr)
  minmax(0, 1.35fr)
  minmax(0, 1.05fr)
`;

const equalColumns = (columns: number) => `repeat(${columns}, minmax(0, 1fr))`;

const EVERY_TILE = '& > *:nth-child(n)';

const firstTileInRow = (columns: number) => `& > *:nth-child(${columns}n + 1)`;

const dividedIntoRowsOf = (columns: number) => `
  ${EVERY_TILE} {
    border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};
    padding-left: 0.75rem;
  }

  ${firstTileInRow(columns)} {
    border-left: none;
    padding-left: 0;
  }
`;

const sixColumnLayout = `
  grid-template-columns: ${SIX_COLUMNS_ORDER_COUPLED_TO_SNAPSHOT_TILE_ORDER};
  ${dividedIntoRowsOf(6)}
`;

const threeColumnLayout = `
  grid-template-columns: ${equalColumns(3)};
  ${dividedIntoRowsOf(3)}
`;

const twoColumnLayout = `
  grid-template-columns: ${equalColumns(2)};
  ${dividedIntoRowsOf(2)}
`;

const Card = styled.section`
  container-type: inline-size;
  container-name: ${CARD_CONTAINER_NAME};
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  background: ${Colours.WHITE};
  border-radius: ${Variables.borderRadius.LARGE};
  box-shadow: ${Variables.boxShadow.alternateBox};
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Eyebrow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: ${Variables.fontSizes.SMALL};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${Colours.OV_PRUSSIAN_BLUE};
`;

const EyebrowDivider = styled.span`
  align-self: stretch;
  width: 1px;
  background: ${Colours.OV_BORDER_COLOR_V3};
`;

const Heading = styled.h1`
  margin: 0;
  font-size: ${Variables.fontSizes.XLARGE};
  font-weight: bold;
  color: ${Colours.OV_BASE};
  overflow-wrap: anywhere;
`;

const Tiles = styled.div`
  display: grid;
  align-items: stretch;
  row-gap: 1.25rem;
  ${twoColumnLayout}

  @media (min-width: ${breakpoints.md}) {
    ${threeColumnLayout}
  }

  @media (min-width: ${breakpoints.lg}) {
    ${sixColumnLayout}
  }

  @container ${CARD_CONTAINER_NAME} (max-width: ${THREE_COLUMN_MAX_CARD_WIDTH_PX}px) {
    ${threeColumnLayout}
  }

  @container ${CARD_CONTAINER_NAME} (max-width: ${TWO_COLUMN_MAX_CARD_WIDTH_PX}px) {
    ${twoColumnLayout}
  }

  @container ${CARD_CONTAINER_NAME} (min-width: ${SIX_COLUMN_MIN_CARD_WIDTH_PX}px) {
    ${sixColumnLayout}
  }
`;
